import { Brain, Search, Eye, TrendingUp, CheckCircle, ArrowRight, MessageSquare, Globe, Zap, ShieldCheck, Target, Users, AlertTriangle, Sparkles } from 'lucide-react';

const AIDiscoveryPage = () => {
  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-gray-900/95 backdrop-blur-sm border-b border-gray-800 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <a href="/" className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent hover:scale-105 transition-transform">
              BragAI
            </a>
            <div className="hidden md:flex space-x-8">
              <a href="/" className="text-gray-300 hover:text-blue-400 transition-colors">Home</a>
              <a href="/use-cases" className="text-gray-300 hover:text-blue-400 transition-colors">Use Cases</a>
              <a href="/ai-discovery" className="text-purple-400">AI Discovery</a>
              <a href="/case-studies" className="text-gray-300 hover:text-blue-400 transition-colors">Case Studies</a>
              <a href="/#contact" className="text-gray-300 hover:text-blue-400 transition-colors">Contact</a>
            </div>
            <button
              onClick={() => window.open('https://calendly.com/georgiahasso/30min', '_blank')}
              className="bg-blue-600 hover:bg-blue-700 px-6 py-2 rounded-lg font-medium transition-colors"
            >
              Book Call
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-purple-600/20 px-4 py-2 rounded-full mb-6">
            <Brain className="w-5 h-5 text-purple-400" />
            <span className="text-purple-300 text-sm font-medium">The Future of Search</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-8 leading-tight">
            <span className="block text-gray-100">How BragAI Works With</span>
            <span className="block bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
              AI Search & Discovery
            </span>
          </h1>
          <p className="text-xl text-gray-300 mb-6 max-w-4xl mx-auto leading-relaxed">
            BragAI is an AI-native platform built for the new era of search. We help businesses get discovered not just on Google, but in AI-generated answers from ChatGPT, Claude, Gemini, and other AI tools.
          </p>
          <p className="text-lg text-gray-400 max-w-3xl mx-auto">
            Understanding how AI discovery works is the first step to positioning your business for the future.
          </p>
        </div>
      </section>

      {/* The Shift in Discovery */}
      <section className="py-16 px-6 bg-gray-800/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-6">How AI-Driven Discovery is Changing Search</h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto">
              Search behavior is fundamentally shifting. Here's what's happening and why it matters for your business.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* Old Way */}
            <div className="bg-gray-900/50 rounded-2xl p-8 border border-gray-700">
              <div className="flex items-center gap-3 mb-6">
                <Search className="w-8 h-8 text-gray-400" />
                <h3 className="text-2xl font-bold text-gray-300">Traditional Search (SEO)</h3>
              </div>
              <ul className="space-y-4">
                <li className="flex items-start gap-3 text-gray-400">
                  <span className="text-gray-500 mt-1">•</span>
                  <span>User types query into Google</span>
                </li>
                <li className="flex items-start gap-3 text-gray-400">
                  <span className="text-gray-500 mt-1">•</span>
                  <span>Google shows 10 blue links</span>
                </li>
                <li className="flex items-start gap-3 text-gray-400">
                  <span className="text-gray-500 mt-1">•</span>
                  <span>User clicks through to websites</span>
                </li>
                <li className="flex items-start gap-3 text-gray-400">
                  <span className="text-gray-500 mt-1">•</span>
                  <span>Success = ranking on page one</span>
                </li>
                <li className="flex items-start gap-3 text-gray-400">
                  <span className="text-gray-500 mt-1">•</span>
                  <span>Optimized with keywords, backlinks, meta tags</span>
                </li>
              </ul>
            </div>

            {/* New Way */}
            <div className="bg-gradient-to-br from-purple-900/30 to-blue-900/30 rounded-2xl p-8 border border-purple-500/30">
              <div className="flex items-center gap-3 mb-6">
                <Brain className="w-8 h-8 text-purple-400" />
                <h3 className="text-2xl font-bold text-white">AI-Driven Discovery (GEO)</h3>
              </div>
              <ul className="space-y-4">
                <li className="flex items-start gap-3 text-gray-200">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <span>User asks ChatGPT, Claude, or Gemini a question</span>
                </li>
                <li className="flex items-start gap-3 text-gray-200">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <span>AI synthesizes an answer from its knowledge</span>
                </li>
                <li className="flex items-start gap-3 text-gray-200">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <span>AI may recommend specific companies or solutions</span>
                </li>
                <li className="flex items-start gap-3 text-gray-200">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <span>Success = being mentioned, recommended, trusted</span>
                </li>
                <li className="flex items-start gap-3 text-gray-200">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <span>Optimized with entity clarity, structured data, authority signals</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-gray-800/60 rounded-xl p-6 border border-gray-700">
            <p className="text-lg text-gray-300 text-center">
              <strong className="text-white">The key insight:</strong> AI systems don't crawl websites like Google. They learn from structured content, clear definitions, consistent brand signals, and authority indicators. If AI doesn't understand who you are and what you do, it can't recommend you.
            </p>
          </div>
        </div>
      </section>

      {/* Why Social Proof Matters for AI */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-6">Why Social Proof Matters in AI Recommendations</h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto">
              Social proof is one of the strongest trust signals for both customers and AI systems. Here's why it's critical for AI discovery.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-gray-800/60 rounded-xl p-6 border border-gray-700 hover:border-purple-500/50 transition-colors">
              <div className="text-purple-400 mb-4">
                <ShieldCheck className="w-10 h-10" />
              </div>
              <h3 className="text-xl font-bold mb-3">Trust Signals</h3>
              <p className="text-gray-400">
                AI systems are trained to recognize credibility. Testimonials, case studies, and reviews signal that real people have validated your business  -  making AI more likely to recommend you.
              </p>
            </div>
            <div className="bg-gray-800/60 rounded-xl p-6 border border-gray-700 hover:border-purple-500/50 transition-colors">
              <div className="text-purple-400 mb-4">
                <Target className="w-10 h-10" />
              </div>
              <h3 className="text-xl font-bold mb-3">Entity Validation</h3>
              <p className="text-gray-400">
                When customers describe what you do in their own words, it reinforces your entity definition. Multiple consistent descriptions help AI understand exactly what category you belong to.
              </p>
            </div>
            <div className="bg-gray-800/60 rounded-xl p-6 border border-gray-700 hover:border-purple-500/50 transition-colors">
              <div className="text-purple-400 mb-4">
                <Globe className="w-10 h-10" />
              </div>
              <h3 className="text-xl font-bold mb-3">Cross-Platform Consistency</h3>
              <p className="text-gray-400">
                Social proof appears across review sites, social media, and directories. This consistent presence across trusted sources strengthens your authority in AI systems.
              </p>
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-600/20 to-blue-600/20 rounded-2xl p-8 border border-purple-500/30">
            <div className="flex items-start gap-4">
              <Sparkles className="w-8 h-8 text-purple-400 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-2xl font-bold mb-3">How BragAI Helps</h3>
                <p className="text-gray-300 text-lg">
                  BragAI helps businesses collect and display testimonials using AI, turning customer feedback into high-impact social proof. We ensure your testimonials are structured for both human visitors and AI systems  -  with schema markup, consistent formatting, and strategic placement that builds trust signals AI can recognize.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How BragAI Strengthens Brand Trust Signals */}
      <section className="py-16 px-6 bg-gray-800/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-6">How BragAI Strengthens Your Brand Trust Signals</h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto">
              BragAI is an AI automation platform designed to maximize your visibility in both traditional search and AI-driven discovery.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="bg-gray-900/50 rounded-xl p-6 border border-gray-700">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-blue-600/20 rounded-lg">
                    <MessageSquare className="w-6 h-6 text-blue-400" />
                  </div>
                  <h3 className="text-xl font-bold">Clear Entity Definition</h3>
                </div>
                <p className="text-gray-400">
                  We ensure your website clearly communicates what your business is, what category it belongs to, and what makes it different  -  using language AI systems understand.
                </p>
              </div>

              <div className="bg-gray-900/50 rounded-xl p-6 border border-gray-700">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-blue-600/20 rounded-lg">
                    <Eye className="w-6 h-6 text-blue-400" />
                  </div>
                  <h3 className="text-xl font-bold">Structured Data & Schema</h3>
                </div>
                <p className="text-gray-400">
                  We implement Organization, Product, FAQ, and other schema types that help machines interpret your site beyond plain text.
                </p>
              </div>

              <div className="bg-gray-900/50 rounded-xl p-6 border border-gray-700">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-blue-600/20 rounded-lg">
                    <Users className="w-6 h-6 text-blue-400" />
                  </div>
                  <h3 className="text-xl font-bold">AI-Readable Content</h3>
                </div>
                <p className="text-gray-400">
                  We structure your content with clear headings, definition-style sentences, and FAQ formats that AI systems can easily parse and reference.
                </p>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-gray-900/50 rounded-xl p-6 border border-gray-700">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-purple-600/20 rounded-lg">
                    <ShieldCheck className="w-6 h-6 text-purple-400" />
                  </div>
                  <h3 className="text-xl font-bold">Authority Building</h3>
                </div>
                <p className="text-gray-400">
                  We help establish your expertise through case studies, testimonials, and content that demonstrates real results  -  signals AI systems associate with credibility.
                </p>
              </div>

              <div className="bg-gray-900/50 rounded-xl p-6 border border-gray-700">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-purple-600/20 rounded-lg">
                    <Zap className="w-6 h-6 text-purple-400" />
                  </div>
                  <h3 className="text-xl font-bold">Smart Dashboards</h3>
                </div>
                <p className="text-gray-400">
                  Our data dashboards help you track key metrics and make data-driven decisions  -  demonstrating professionalism that builds trust with customers.
                </p>
              </div>

              <div className="bg-gray-900/50 rounded-xl p-6 border border-gray-700">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-purple-600/20 rounded-lg">
                    <TrendingUp className="w-6 h-6 text-purple-400" />
                  </div>
                  <h3 className="text-xl font-bold">Consistent Brand Signals</h3>
                </div>
                <p className="text-gray-400">
                  We ensure your brand description, value proposition, and positioning are consistent across your website, profiles, and all digital touchpoints.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* GEO vs SEO */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-6">GEO vs SEO: Do You Need Both?</h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto">
              Generative Engine Optimization (GEO) and Search Engine Optimization (SEO) serve different purposes. Here's how they compare.
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full bg-gray-800/50 rounded-xl border border-gray-700">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left p-4 text-gray-400 font-medium">Aspect</th>
                  <th className="text-left p-4 text-gray-400 font-medium">SEO (Google)</th>
                  <th className="text-left p-4 text-purple-400 font-medium">GEO (AI Systems)</th>
                </tr>
              </thead>
              <tbody className="text-gray-300">
                <tr className="border-b border-gray-700/50">
                  <td className="p-4 font-medium">Goal</td>
                  <td className="p-4">Rank on page one of search results</td>
                  <td className="p-4">Be mentioned in AI-generated answers</td>
                </tr>
                <tr className="border-b border-gray-700/50">
                  <td className="p-4 font-medium">How it works</td>
                  <td className="p-4">Keywords, backlinks, page speed</td>
                  <td className="p-4">Entity clarity, structured data, authority</td>
                </tr>
                <tr className="border-b border-gray-700/50">
                  <td className="p-4 font-medium">User behavior</td>
                  <td className="p-4">Click through to your website</td>
                  <td className="p-4">Get answer directly from AI</td>
                </tr>
                <tr className="border-b border-gray-700/50">
                  <td className="p-4 font-medium">Competition</td>
                  <td className="p-4">10 spots on page one</td>
                  <td className="p-4">1-3 recommendations per answer</td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Timeline</td>
                  <td className="p-4">Months to see results</td>
                  <td className="p-4">Depends on AI model updates</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-8 bg-gray-800/60 rounded-xl p-6 border border-gray-700">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
              <p className="text-gray-300">
                <strong className="text-white">The answer: yes, you need both.</strong> SEO remains important for traditional search traffic. But as more users turn to AI for answers, GEO becomes essential for staying visible. BragAI helps businesses optimize for both  -  building websites that rank well and content that AI systems trust and recommend.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-r from-purple-600/10 to-blue-600/10">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Be Discovered by AI?</h2>
          <p className="text-xl text-gray-300 mb-4">
            BragAI helps businesses prepare for the future of discovery  -  today.
          </p>
          <p className="text-lg text-gray-400 mb-12">
            Let's optimize your brand for both traditional search and AI-driven discovery.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => window.open('https://calendly.com/georgiahasso/30min', '_blank')}
              className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 px-10 py-4 rounded-lg font-medium text-lg transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-purple-500/25 flex items-center justify-center gap-2"
            >
              Get Started with GEO <ArrowRight className="w-5 h-5" />
            </button>
            <a
              href="/#geo"
              className="border border-gray-600 hover:border-gray-500 px-10 py-4 rounded-lg font-medium text-lg transition-all duration-300 hover:scale-105 text-center"
            >
              Learn More About GEO
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-gray-800">
        <div className="max-w-6xl mx-auto text-center">
          <div className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent mb-4">
            BragAI
          </div>
          <p className="text-gray-300 mb-2">
            BragAI is used by startups, agencies, and e-commerce brands to increase conversions and build credibility.
          </p>
          <p className="text-gray-400 mb-6">
            AI Automation Services • Modern Websites • Smart Dashboards • GEO Optimization
          </p>
          <div className="text-gray-500">
            © 2025 BragAI. Built for businesses that think forward.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AIDiscoveryPage;
